import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { getApiBaseUrl } from '../../config/apiBase';
import { 
  Settings, 
  Save,
  X,
  RefreshCw,
  Server,
  Cpu,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info,
  Zap,
  Download,
  Upload,
  FileText
} from 'lucide-react';

const WorkerConfiguration = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [configData, setConfigData] = useState({});
  const [originalConfig, setOriginalConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchWorkers();
  }, []);

  useEffect(() => {
    // Check for changes
    const changed = JSON.stringify(configData) !== JSON.stringify(originalConfig);
    setHasChanges(changed);
  }, [configData, originalConfig]);

  const fetchWorkers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWorkers(data || []);
      } else if (response.status === 403) {
        toast.error('Access denied. Master Admin role required.');
      } else {
        toast.error('Failed to fetch workers');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
      toast.error('Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWorkers();
    setRefreshing(false);
  };

  const handleSelectWorker = async (worker) => {
    setSelectedWorker(worker);
    setConfigData({});
    setOriginalConfig({});
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${worker.code}/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfigData(data.config || {});
        setOriginalConfig(JSON.parse(JSON.stringify(data.config || {})));
      } else {
        toast.error('Failed to fetch worker configuration');
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to fetch worker configuration');
    }
  };

  const handleConfigChange = (key, value) => {
    setConfigData({
      ...configData,
      [key]: value
    });
  };

  const handleSaveConfig = async () => {
    if (!selectedWorker) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${selectedWorker.code}/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configData)
      });
      
      if (response.ok) {
        toast.success('Configuration saved successfully');
        setOriginalConfig(JSON.parse(JSON.stringify(configData)));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetConfig = () => {
    setConfigData(JSON.parse(JSON.stringify(originalConfig)));
  };

  const getConfigIcon = (key) => {
    if (key.includes('batch') || key.includes('size')) return <Server className="w-4 h-4" />;
    if (key.includes('interval') || key.includes('timeout')) return <Clock className="w-4 h-4" />;
    if (key.includes('retry') || key.includes('memory')) return <RefreshCw className="w-4 h-4" />;
    if (key.includes('cpu') || key.includes('process')) return <Cpu className="w-4 h-4" />;
    return <Settings className="w-4 h-4" />;
  };

  const getConfigType = (key, value) => {
    if (key.includes('enable') || key.includes('active')) return 'boolean';
    if (key.includes('interval') || key.includes('timeout') || key.includes('seconds') || key.includes('retry') || key.includes('size') || key.includes('count') || key.includes('memory')) return 'number';
    if (key.includes('level')) return 'select';
    return 'text';
  };

  const renderConfigInput = (key, value) => {
    const type = getConfigType(key, value);
    
    switch (type) {
      case 'boolean':
        return (
          <select
            value={value ? 'true' : 'false'}
            onChange={(e) => handleConfigChange(key, e.target.value === 'true')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleConfigChange(key, parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
      
      case 'select':
        if (key.includes('level')) {
          return (
            <select
              value={value}
              onChange={(e) => handleConfigChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="DEBUG">Debug</option>
              <option value="INFO">Info</option>
              <option value="WARNING">Warning</option>
              <option value="ERROR">Error</option>
            </select>
          );
        }
        break;
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleConfigChange(key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
    }
    
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleConfigChange(key, e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    );
  };

  const handleExportConfig = () => {
    const configStr = JSON.stringify(configData, null, 2);
    const blob = new Blob([configStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedWorker?.code}_config_${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Configuration exported successfully');
  };

  const handleImportConfig = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const config = JSON.parse(event.target.result);
            setConfigData(config);
            toast.success('Configuration imported successfully');
          } catch (error) {
            toast.error('Invalid configuration file');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Settings className="w-8 h-8 text-indigo-600" />
              Worker Configuration
            </h1>
            <p className="text-gray-600 mt-2">Configure and manage worker settings</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportConfig}
              disabled={!selectedWorker}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors duration-200 shadow-sm"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={handleImportConfig}
              disabled={!selectedWorker}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors duration-200 shadow-sm"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors duration-200 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Worker Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Worker</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {workers.map((worker) => (
            <button
              key={worker.code}
              onClick={() => handleSelectWorker(worker)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedWorker?.code === worker.code
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  worker.status === 'running' 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {worker.status === 'running' ? <CheckCircle className="w-5 h-5" /> : <Server className="w-5 h-5" />}
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{worker.name}</h3>
                  <p className="text-sm text-gray-600">{worker.code}</p>
                  <p className="text-xs text-gray-500 mt-1">{worker.status}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Configuration Panel */}
      {selectedWorker && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Configuration: {selectedWorker.name}</h2>
              <p className="text-sm text-gray-600 mt-1">Modify worker settings and parameters</p>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <span className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-full">
                  Unsaved changes
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => {
                setConfigData(originalConfig);
                setSelectedWorker(null);
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
      
      {!selectedWorker && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Worker Selected</h3>
          <p className="text-gray-600">Select a worker from the list to view and configure its parameters</p>
        </div>
      )}
    </div>
  );
};

export default WorkerConfiguration;
